"use client";
/**
 * A persisted workouts block, drawn by the workouts family.
 *
 * The card owns no composition of its own. It sits on the shared attach plate —
 * the same material `CattachedresultParsedItems` draws every other block on, so
 * two blocks in one version body are one surface — and inside it mounts
 * `CworkoutcardSet` for the movements and `CworkoutcardPlan` for the sessions.
 * Everything a reader sees below the plate is the family's own geometry.
 *
 * THE DEMONSTRATION LEADS, and that is why the composition is PINNED rather
 * than read off the tweak registry. A movement is attached to a deliverable
 * because someone wants to watch it done correctly; `Demo frame` puts the
 * player at the top of the card, and the frame arms against the block's own
 * durable address, which resolves a live URL at click time and has no expiry to
 * outlive.
 *
 * A block whose items did not read as this pillar's shape draws the lossless
 * render instead. The shared reader has already degraded such a block whole
 * (D28), so the fallback is a fact about the block rather than a failure here.
 */
import type { ReactElement } from "react";
import { IconBarbell } from "@tabler/icons-react";
import type { ParsedAttachResults } from "@cabal/shared/domain/attached-result/index.js";
import {
  CresultcardLayout,
  type CresultcardSetEmpty,
} from "@/components/ui/Cresultcard";
import {
  CworkoutcardPlan,
  CworkoutcardSet,
  CworkoutcardVariant,
} from "@/components/ui/Cworkoutcard";
import { CATTACHEDRESULT_PLATE_STYLE } from "./cattachedresult.plate";
import { cattachedresultWorkouts } from "./cattachedresult.workouts.model";
import { CattachedresultParsedItems } from "./CattachedresultParsedItems";

const SET_LABEL = "Exercises";
const BLOCK_LABEL = "Attached exercises";
const EMPTY: CresultcardSetEmpty = {
  headline: "No movements in this block",
  reason: "The attached search stored a session rather than a catalog row.",
};

export interface CattachedresultWorkoutsProps {
  readonly block: ParsedAttachResults;
}

export function CattachedresultWorkouts({
  block,
}: CattachedresultWorkoutsProps): ReactElement {
  const { exercises, plans } = cattachedresultWorkouts({ block });

  if (exercises.length === 0 && plans.length === 0) {
    return <CattachedresultParsedItems block={block} />;
  }

  return (
    <section
      aria-label={block.about ?? BLOCK_LABEL}
      style={CATTACHEDRESULT_PLATE_STYLE}
    >
      {exercises.length > 0 && (
        <CworkoutcardSet
          context={block.about ?? undefined}
          drillContext={block.about ?? BLOCK_LABEL}
          empty={EMPTY}
          exercises={exercises}
          icon={IconBarbell}
          label={SET_LABEL}
          // Cards, because a card is the unit that carries a demonstration
          // frame; the row and the rail card are comparison objects and neither
          // mounts a player.
          layout={CresultcardLayout.Cards}
          provenance={block.capturedAtLabel ?? undefined}
          variant={CworkoutcardVariant.DemoFrame}
        />
      )}
      {plans.map((plan) => (
        <CworkoutcardPlan key={plan.key} plan={plan} />
      ))}
    </section>
  );
}
